/** @jsx jsx */
import { Fragment, h } from 'preact';
import { useState, useMemo, useCallback } from 'preact/hooks';

// Searchspring
import { Badge, Price, Image, useMediaQuery, Button } from '@searchspring/snap-preact-components';
import { useTheme, CacheProvider } from '@searchspring/snap-preact-components/dist/cjs/providers';
import { defined, cloneWithProps } from '@searchspring/snap-preact-components/dist/cjs/utilities';
import { filters } from '@searchspring/snap-toolbox';

// Packages
import { jsx, css } from '@emotion/react';
import classnames from 'classnames';
import { observer } from 'mobx-react-lite';


// Helpers
import { useSwatches, usePrices } from '../../helpers';

// CSS
import './Result.css';
import { Wedge } from '../Recommendations/Wedge';

const CSS = {
  result: () =>
    css({
      '&.ss__result--grid': {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        '& .ss__result__image-wrapper': {
          flex: '1 0 auto',
        },
      },
      '&.ss__result--list': {
        display: 'flex',
        flexDirection: 'row',
        '& .ss__result__image-wrapper': {
          flex: '0 0 33%',
        },
        '& .ss__result__details': {
          flex: '1 1 auto',
          textAlign: 'left',
          marginLeft: '20px',
          padding: 0,
        },
      },

      '& .ss__result__image-wrapper': {
        position: 'relative',
        '& .ss__result__badge': {
          background: 'rgba(255, 255, 255, 0.5)',
          padding: '0.8rem 1.4rem',
        },
      },

      '& .ss__result__details': {
        padding: '10px',
        textAlign: 'center',

        '& .ss__result__details__title': {
          marginBottom: '0.6rem',
        },
      },
    }),
};

export const Result = observer(properties => {
  const globalTheme = useTheme();

  const props = {
    layout: 'grid',
    ...globalTheme?.components?.result,
    ...properties,
    ...properties.theme?.components?.result,
  };

  const {
    result,
    hideBadge,
    hideBrand,
    hidePricing,
    hideProductLine,
    hidePromo,
    hideSwatches,
    hideTitle,
    detailSlot,
    fallback,
    disableStyles,
    className,
    layout,
    style,
    controller,
    isRecommendations,
  } = props;

  let bannerValue = result?.value;
  const ssConfig = controller?.store?.config?.globals?.ssConfig ?? {};
  const { hideResultBackgrounds, squareResultAspectRatio, hideQuickView } = ssConfig;

  
  const core = result?.mappings?.core;
  const badge = result?.attributes?.mfield_cql_badge ? result.attributes.mfield_cql_badge : null;
  const hideQuicView = result?.attributes?.mfield_cql_hide_quick_view ? result.attributes.mfield_cql_hide_quick_view : false;
  const brand = core?.brand;
  const line = result?.attributes?.mfield_cql_line ? result.attributes.mfield_cql_line : null;
  const age = result?.attributes?.mfield_cql_age_range ? result.attributes.mfield_cql_age_range : null;
  const productID = core.uid;
  const variantID = result?.attributes?.variant_id ? result?.attributes?.variant_id : productID;
  const variantTitle = result?.attributes?.variant_title;
  let variantColors = result?.attributes?.variant_color;
  let variantColorsSliced = '';
  if (variantColors) {
    variantColorsSliced = variantColors.slice();
  }
  let variantTitleArray = '';
  let variantIDArray = '';

  const { minPrice, maxPrice, maxCompareAtPrice, variantPrices } = usePrices(result);

  const { swatches, hasSwatchImages } = useSwatches(result);
  const [selectedSwatchIndex, setSelectedSwatchIndex] = useState(0);

  const variantIdString = typeof variantID === 'string' ? variantID : String(variantID ?? '');
  const isSingleVariant = variantIdString !== '' && !variantIdString.includes('|');

  const isOutOfStock = useMemo(() => {
    const attrs = result?.attributes || {};
    const coreMapping = result?.mappings?.core || {};

    const boolLike = (value) => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value !== 0;
      if (typeof value === 'string') {
        const v = value.trim().toLowerCase();
        if (['true', 'yes', 'y', '1'].includes(v)) return true;
        if (['false', 'no', 'n', '0'].includes(v)) return false;
      }
      return undefined;
    };

    const numberLike = (value) => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) return Number(value);
      return undefined;
    };

    // Common feed fields
    const available =
      boolLike(attrs.available) ??
      boolLike(attrs.in_stock) ??
      boolLike(attrs.inStock) ??
      boolLike(coreMapping.available) ??
      boolLike(coreMapping.in_stock) ??
      undefined;

    if (available === false) return true;
    if (available === true) return false;

    const inventoryQty =
      numberLike(attrs.inventory_quantity) ??
      numberLike(attrs.inventory_qty) ??
      numberLike(attrs.qty) ??
      numberLike(attrs.stock) ??
      numberLike(coreMapping.stock) ??
      undefined;

    if (typeof inventoryQty === 'number') return inventoryQty <= 0;

    const stockMessage = (attrs.stock_message || attrs.stockMessage || '').toString().toLowerCase();
    if (stockMessage.includes('out of stock') || stockMessage.includes('sold out') || stockMessage.includes('unavailable')) {
      return true;
    }

    return false;
  }, [result]);

  let hrefMatch = '';
  if (bannerValue != undefined && result?.type == 'banner') {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = bannerValue; // Parse the string into a DOM structure
    // Find the <a> and <img> elements inside the parsed HTML

    const anchor = tempElement.querySelector('a');
    const img = tempElement.querySelector('img');
    // Safely extract the href, src, alt, and title attributes
    hrefMatch = anchor ? anchor.getAttribute('href') : '';
    const srcMatch = img ? img.getAttribute('src') : '';
    core.imageUrl = srcMatch;
  }


  
  const images = result?.attributes?.images || [];
  let defaultImage = result?.core?.imageUrl;
  let hoverImageUrl = null;
  let enableSecondImageHover = ssConfig.enableSecondImageHover ?? false;
  
  if(enableSecondImageHover == "true") {
    if (swatches?.length > 0 && selectedSwatchIndex < swatches.length) {
      const selectedSwatch = swatches[selectedSwatchIndex];
      const selectedColorLabel = selectedSwatch?.label?.toLowerCase();
      defaultImage = selectedSwatch?.productImg || defaultImage;
      
      hoverImageUrl = images.find(
        image =>
        image.toLowerCase().includes(selectedColorLabel) &&
        image !== defaultImage
      ) || hoverImageUrl;
  
      if(!hoverImageUrl) {
        hoverImageUrl = images.find(image => image !== defaultImage) || null;
      }
    } else {
      hoverImageUrl = images.find((image, index) => image !== defaultImage && (images.length > 1 ? index === 1 : index === 0)) || null;
    }
  }

  const subProps = {
    price: {
      className: 'ss__result__price p2-alt',
      ...globalTheme?.components?.price,
      ...defined({
        disableStyles,
      }),
      theme: props.theme,
    },
    badge: {
      className: 'ss__result__badge p4-alt',
      content: badge,
      ...globalTheme?.components?.badge,
      ...defined({
        disableStyles,
      }),
      theme: props.theme,
    },
    image: {
      className: 'ss__result__image',
      alt: core?.name,
      src: swatches?.[selectedSwatchIndex]?.productImg ?? core?.imageUrl,
      ...globalTheme?.components?.image,
      ...defined({
        disableStyles,
        fallback: fallback,
      }),
      hoverSrc: enableSecondImageHover == "true" ? hoverImageUrl : undefined,
      theme: props.theme,
    },
    imageBanner: {
      className: 'ss__banner__image__wrapper visibility-hidden',
      alt: core?.name,
      src: swatches?.[selectedSwatchIndex]?.productImg ?? core?.imageUrl,
      ...globalTheme?.components?.image,
      ...defined({
        disableStyles,
        fallback: fallback,
      }),
      theme: props.theme,
    },
  };
   // Capture the correct currency symbol from shop currency 
  // Capture the desired placement from theme setting
  let currencySymbolAfter;
  let currencySymbol;
  if (ssConfig.currencyPlacement == "before") {
    currencySymbolAfter = false;
    currencySymbol = ssConfig.shopCurrency;
  } else {
    currencySymbolAfter = true;
    currencySymbol = `\u00A0${ssConfig.shopCurrency}`;
  }
  
  let displayName = core.name;
  if (props.truncateTitle) {
    displayName = filters.truncate(core.name, props.truncateTitle.limit, props.truncateTitle.append);
  }

  const styling = {};
  if (!disableStyles) {
    styling.css = [CSS.result(), style];
  } else if (style) {
    styling.css = [style];
  }

  const transformedUrl = core?.url || '/';

  if (!isRecommendations) {
    const variantIdSplit = function (variantID, variantIDArray, productID) {
      if (variantID != undefined && Symbol.iterator in Object(variantID)) {
        variantIDArray = variantID.split('|');
        return variantIDArray;
      } else if (variantID != undefined && Symbol.iterator in Object(variantID) == false) {
        variantIDArray = variantID;
        return variantIDArray;
      }
    };

    //To grab correct Variant Id from array, find which color is selected, and then compare index to variantIdArray
    const variantTitleSplit = function (variantTitle, variantTitleArray) {
      if (variantTitle != undefined && Symbol.iterator in Object(variantTitle)) {
        variantTitleArray = variantTitle.split('|');
        return variantTitleArray;
      } else if (variantTitle != undefined && Symbol.iterator in Object(variantTitle) == false) {
        variantTitleArray = variantTitle;
        return variantTitleArray;
      }
    };

    variantIDArray = variantIdSplit(variantID, variantIDArray, productID);
    variantTitleArray = variantTitleSplit(variantTitle, variantTitleArray);
    if (variantIDArray == undefined) variantIDArray = [];
    if (variantTitleArray == undefined) variantTitleArray = [];

    let variantNameSelected = [...variantColorsSliced][selectedSwatchIndex];

    let foundIndex = variantTitleArray.findIndex(ele => ele.includes(variantNameSelected));
    if (foundIndex != undefined) var variantIdSelected = [...variantIDArray][foundIndex];
  }

  let cardURL = variantIdSelected ? transformedUrl + '?variant=' + variantIdSelected : transformedUrl;
  if (result?.type == 'banner') {
    cardURL = hrefMatch;
  }

  return (
    <CacheProvider>
      <article {...styling} className={classnames('ss__result', `ss__result--${layout}`, className)}>
        <a
          // href={transformedUrl + queryParam}
          href={cardURL}
          onMouseDown={e => {

            controller?.track?.product?.click(e, result);
            // Store scroll position and pagination state before navigating
            // Only store if we're on a collection page (collections/{collection})
            if (typeof window !== 'undefined' && window.sessionStorage) {
              const currentPath = window.location.pathname;
              const isCollectionPage = /^\/collections\/[^\/]+/.test(currentPath);
              
              if (isCollectionPage) {
                const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
                const pagination = controller?.store?.pagination;
                // Store the full URL including query parameters (e.g., ?p=2)
                const fullUrl = window.location.href;
                sessionStorage.setItem('searchspring_scroll_position', scrollPosition.toString());
                sessionStorage.setItem('searchspring_saved_url', fullUrl);
                if (pagination) {
                  sessionStorage.setItem('searchspring_page_number', pagination.current.number.toString());
                  sessionStorage.setItem('searchspring_total_pages', pagination.totalPages.toString());
                }
              }
            }
            
          }}
          className={`ss__result-link ${result.type === 'banner' ? 'ss__banner__height' : ''}`}
          aria-label={displayName}
          onMouseEnter={e => {
            const quickViewButton = e.currentTarget.querySelector('.ss__result__quick-view-trigger');
            if (quickViewButton) {
              quickViewButton.style.opacity = 1;
            }
          }}
          onMouseLeave={e => {
            const quickViewButton = e.currentTarget.querySelector('.ss__result__quick-view-trigger');
            if (quickViewButton) {
              quickViewButton.style.opacity = 0;
            }
          }}
        >
          {result.type === 'banner' ? (
            // Full-width image for banner type
            <Image {...subProps.imageBanner} />
          ) : (
            <div className='product-card-top-wrapper'>
              <div className='ss__result__image-wrapper'>
                {isRecommendations ? <Wedge /> : null}
                {!hideBadge && <Badge {...subProps.badge} />}
                {!hideQuickView && !hideQuicView && (!isSingleVariant || !isOutOfStock) && (
                  <Button
                    className="ss__result__quick-view-trigger"
                    data-handle={result?.mappings?.core?.url}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Trigger custom event for quick view
                      const event = new CustomEvent('ssQuickViewClick', {
                        detail: {
                          productUrl: result?.mappings?.core?.url,
                          productName: core?.name,
                        },
                      });
                      window.dispatchEvent(event);
                    }}
                  >
                  {variantIdString && variantIdString.includes('|') ? (
                    'QUICK VIEW'
                  ) : (
                    'ADD TO CART'
                  )}

                  </Button>
                )}
                <div
                  {...styling}
                  className={classnames(
                    'ss__image',
                    'ss__result__image',
                    {
                      'ss__result__image--hide-bg': hideResultBackgrounds,
                    },
                    {
                      'ss__result__image--square': squareResultAspectRatio,
                    }
                  )}
                >
                  <Image {...subProps.image} />
                </div>
              </div>
              <div className='ss__result__details'>
                {!hideSwatches && !ssConfig.hideSwatches && swatches.length > 1 ? (
                  <ColorSwatches
                    swatches={swatches}
                    setSelectedSwatchIndex={setSelectedSwatchIndex}
                    selectedSwatchIndex={selectedSwatchIndex}
                    hasSwatchImages={hasSwatchImages}
                  />
                ) : null}

                {!hideBrand && brand && <div className='ss__result__details__brand'>{brand}</div>}

                {line != null && (
                  <div className='ss__result__details__line__wrapper'>
                    {line in window.Resources.searchspring.logos ? (
                      <img
                        className='ss__result__details__line__image'
                        src={window.Resources.searchspring.logos[line]}
                        alt={line}
                        height={12}
                        loading='lazy'
                      />
                    ) : null}
                  </div>
                )}
                {!hideTitle && (
                  <div
                    className='ss__result__details__title h5-regular'
                    dangerouslySetInnerHTML={{
                      __html: displayName,
                    }}
                  />
                )}

                {age != null && (
                  <div
                    className='ss__result__details__age p4-alt'
                    dangerouslySetInnerHTML={{
                      __html: age,
                    }}
                  />
                )}


                {!hidePricing && (
                  <div className='ss__result__details__pricing'>
                    {minPrice != null &&
                    variantPrices.length > 0 &&
                    maxCompareAtPrice != null &&
                    minPrice < maxCompareAtPrice ? (
                      <div className='ss__result__details__compare'>
                        <Price {...subProps.price} value={minPrice} symbol={currencySymbol} symbolAfter={currencySymbolAfter} />
                        &nbsp;
                        <Price {...subProps.price} value={maxCompareAtPrice} lineThrough={true} symbol={currencySymbol} symbolAfter={currencySymbolAfter} />
                      </div>
                    ) : minPrice != null &&
                      variantPrices.length > 1 &&
                      maxCompareAtPrice == null &&
                      minPrice != maxPrice ? (
                      <div className='ss__result__details__range'>
                        <Price {...subProps.price} value={minPrice} symbol={currencySymbol} symbolAfter={currencySymbolAfter} />

                        <span> - </span>

                        <Price {...subProps.price} value={maxPrice} symbol={currencySymbol} symbolAfter={currencySymbolAfter} />
                      </div>
                    ) : (
                      <>
                        <Price {...subProps.price} value={core.price} symbol={currencySymbol} symbolAfter={currencySymbolAfter} />
                      </>
                    )}
                  </div>
                )}
                {cloneWithProps(detailSlot, { result })}
              </div>
            </div>
          )}
        </a>
      </article>
    </CacheProvider>
  );
});

const ColorSwatches = ({ swatches, hasSwatchImages, setSelectedSwatchIndex, selectedSwatchIndex }) => {
  const isMobile = useMediaQuery('(max-width: 449px)');
  const maxSwatches = isMobile ? 3 : 5;
  const extraSwatchCount = swatches.length - maxSwatches;

  const fetchImg = useCallback(swatch => () => {
    if (swatch.productImg) {
      window.quicklink?.prefetch(swatch.productImg);
    }
  });

  return (
    <div className='ss__swatches'>
      {swatches.map((swatch, index) => {
        if (index === maxSwatches) return <div className='ss__swatch-extra-count p4'>+{extraSwatchCount}</div>;
        if (index > maxSwatches) return null;

        return (
          <div
            className={classnames(
              'ss__swatch',
              {
                'ss__swatch--selected': hasSwatchImages && selectedSwatchIndex === index,
              },
              {
                'ss__swatch--no-images': !hasSwatchImages,
              }
            )}
            onClick={e => {
              if (hasSwatchImages) {
                e.preventDefault();
                setSelectedSwatchIndex(index);
              }
            }}
            onLoad={e => {
              if (hasSwatchImages) {
                e.preventDefault();
                setSelectedSwatchIndex(index);
              }
            }}
            onMouseOver={fetchImg(swatch)}
            onFocus={fetchImg(swatch)}
            onTouchStart={fetchImg(swatch)}
          >
            <div
              className={`ss__swatch-inner ss__swatch-border-${swatch.label}`}
              style={{
                background: swatch.hex,
              }}
            />
          </div>
        );
      })}
    </div>
  );
};
